/**
 * ChatSidebar — floating overlay communication panel (no layout push).
 *
 * States:
 *   hidden     — nothing rendered; toggled by "v3.chatSidebar.toggle" event
 *   list       — 260px channel list
 *   open       — 480px list (narrow) + message pane side-by-side
 *   pane-only  — 300px message pane only (list hidden)
 *
 * Toggled from ChatWidget via window.dispatchEvent(new CustomEvent("v3.chatSidebar.toggle")).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft, ChevronRight, MessageSquare, Hash,
  User, Send, Loader2, Pencil, Trash2, X, ChevronDown, Building2, Search, Globe,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const COLLAPSED_W = 48;
const LIST_W = 260;
const LIST_NARROW_W = 180;
const PANE_W = 300;
const OPEN_W = LIST_NARROW_W + PANE_W;
const POLL_MS = 10_000;

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString())
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function initials(name, email) {
  return ((name || email || "?").split(/\s|@/).filter(Boolean)
    .slice(0, 2).map((s) => s[0].toUpperCase()).join(""));
}

// ─── Message row ────────────────────────────────────────────────────────────

function MsgRow({ m, canEdit, canDelete, onEdit, onDelete }) {
  if (m.is_system) {
    return (
      <div className="px-3 py-1.5 flex justify-center">
        <div className="text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-1 text-center max-w-full">
          {m.body}
          <span className="ml-1.5 text-slate-400">{fmtTime(m.created_at)}</span>
        </div>
      </div>
    );
  }
  const ini = initials(m.author_name, m.author_email);
  const body = (m.body || "").split(/(@[a-zA-Z0-9._\-+@]{2,80})/g).map((p, i) =>
    p.startsWith("@")
      ? <span key={i} className="text-blue-600 font-medium text-xs">{p}</span>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );

  return (
    <div className="flex gap-2 px-3 py-2 hover:bg-slate-50 group">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
        {ini}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="text-xs font-semibold text-slate-900 truncate">
            {m.author_name || m.author_email || "Unknown"}
          </span>
          <span className="text-[10px] text-slate-400 shrink-0">{fmtTime(m.created_at)}</span>
          {m.edited_at && <span className="text-[10px] text-slate-400">(edited)</span>}
        </div>
        <div className="text-xs text-slate-800 whitespace-pre-wrap break-words leading-relaxed mt-0.5">
          {m.deleted_at ? <span className="italic text-slate-400">[deleted]</span> : body}
        </div>
      </div>
      {!m.deleted_at && (
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 shrink-0">
          {canEdit && (
            <button type="button" onClick={() => onEdit(m)}
              className="text-slate-400 hover:text-blue-600 p-0.5">
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {canDelete && (
            <button type="button" onClick={() => onDelete(m)}
              className="text-slate-400 hover:text-rose-600 p-0.5">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Channel icon ────────────────────────────────────────────────────────────

function ChannelIcon({ className = "w-4 h-4" }) {
  return <Hash className={className} />;
}

// ─── Message pane ────────────────────────────────────────────────────────────

function MessagePane({ channel, onClose, onShowList, listHidden }) {
  const { user, isAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const textareaRef = useRef(null);

  // @-mention state
  const [members, setMembers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionAnchorStart, setMentionAnchorStart] = useState(-1);
  const [mentionSelected, setMentionSelected] = useState(0);

  // Load channel members for @mention picker.
  // Scoped by channel kind; falls back to all workspace members if the
  // channel membership table is empty (e.g. first access before auto-join commits).
  useEffect(() => {
    if (!channel?.id) return;
    setMembers([]);
    v2Api.channels.listMembers(channel.id)
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) {
          setMembers(list);
        } else {
          // channel has no membership rows yet — fall back to workspace members
          return v2Api.workspaceMembers().then((ws) => {
            setMembers(
              (Array.isArray(ws) ? ws : []).map((u) => ({
                user_id: u.id,
                name: u.name,
                email: u.email,
                role: u.role || "member",
              }))
            );
          });
        }
      })
      .catch(() => {
        v2Api.workspaceMembers().then((ws) => {
          setMembers(
            (Array.isArray(ws) ? ws : []).map((u) => ({
              user_id: u.id,
              name: u.name,
              email: u.email,
              role: u.role || "member",
            }))
          );
        }).catch(() => {});
      });
  }, [channel?.id]);

  const mentionSuggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = (mentionQuery || "").toLowerCase();
    return members
      .filter((m) => `${m.name || ""} ${m.email || ""}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [members, mentionQuery]);

  const closeMention = () => { setMentionQuery(null); setMentionAnchorStart(-1); setMentionSelected(0); };

  const insertMention = (member) => {
    const displayName = (member.name || member.email || "").split("@")[0].replace(/\s+/g, "");
    const before = composer.slice(0, mentionAnchorStart);
    const cursorPos = textareaRef.current?.selectionStart ?? composer.length;
    const after = composer.slice(cursorPos);
    const newVal = `${before}@${displayName} ${after}`;
    setComposer(newVal);
    closeMention();
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = before.length + displayName.length + 2;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const onComposerChange = (e) => {
    const val = e.target.value;
    setComposer(val);
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionAnchorStart(cursor - match[0].length);
      setMentionSelected(0);
    } else {
      closeMention();
    }
  };

  const load = useCallback(async () => {
    if (!channel?.id) return;
    try {
      const data = await v2Api.channels.getMessages(channel.id);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("chat load failed", e);
    } finally {
      setLoading(false);
    }
  }, [channel?.id]);

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    closeMention();
    setComposer("");
    load();
    if (channel?.id) v2Api.channels.markRead(channel.id).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.id, load]);

  useEffect(() => {
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const msgCount = messages.length;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgCount]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = composer.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      if (editTarget) {
        const updated = await v2Api.channels.editChannelMessage(channel.id, editTarget.id, body);
        setMessages((prev) => prev.map((m) => m.id === editTarget.id ? updated : m));
        setEditTarget(null);
      } else {
        const created = await v2Api.channels.sendMessage(channel.id, { body });
        setMessages((prev) => [...prev, created]);
        v2Api.channels.markRead(channel.id).catch(() => {});
      }
      setComposer("");
      closeMention();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionSelected((s) => Math.min(s + 1, mentionSuggestions.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionSelected((s) => Math.max(s - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionSuggestions[mentionSelected]); return; }
      if (e.key === "Escape") { e.preventDefault(); closeMention(); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key === "Escape" && editTarget) { setEditTarget(null); setComposer(""); }
  };

  const startEdit = (m) => { setEditTarget(m); setComposer(m.body); };
  const deleteMsg = async (m) => {
    if (!await confirm("Delete this message?", { title: "Delete message", confirmLabel: "Delete" })) return;
    try {
      await v2Api.channels.deleteChannelMessage(channel.id, m.id);
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, deleted_at: new Date().toISOString() } : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      {/* Pane header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50 shrink-0">
        {listHidden && (
          <button type="button" onClick={onShowList} className="text-slate-400 hover:text-slate-600 p-0.5" title="Show list">
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        <ChannelIcon className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800 flex-1 truncate">
          {channel.display_name}
        </span>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-0.5" title="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-1">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-xs">Loading…</span>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center py-10 gap-2 text-center px-4">
            <MessageSquare className="w-8 h-8 text-slate-200" />
            <p className="text-xs text-slate-500">No messages yet. Start the conversation.</p>
          </div>
        )}
        {messages.map((m) => (
          <MsgRow
            key={m.id}
            m={m}
            canEdit={m.author_id === user?.id || m.author_email === user?.email}
            canDelete={isAdmin?.() || m.author_id === user?.id || m.author_email === user?.email}
            onEdit={startEdit}
            onDelete={deleteMsg}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-slate-100 px-2 py-2 bg-white relative">
        {/* @-mention dropdown — floats above the composer */}
        {mentionSuggestions.length > 0 && (
          <div className="absolute bottom-full left-2 right-2 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-20">
            <div className="px-2 py-1 text-[10px] text-slate-400 uppercase tracking-wide border-b border-slate-100">
              Mention
            </div>
            {mentionSuggestions.map((m, idx) => {
              const ini = initials(m.name, m.email);
              return (
                <button
                  key={m.user_id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${idx === mentionSelected ? "bg-blue-50" : "hover:bg-slate-50"}`}
                >
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold flex items-center justify-center shrink-0">
                    {ini}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-800 truncate">{m.name || m.email}</div>
                    {m.name && <div className="text-[10px] text-slate-400 truncate">{m.email}</div>}
                  </div>
                  <div className="shrink-0 text-[10px] text-slate-400">{m.role}</div>
                </button>
              );
            })}
          </div>
        )}
        {editTarget && (
          <div className="flex items-center justify-between text-[10px] text-blue-600 bg-blue-50 rounded px-2 py-1 mb-1.5">
            <span>Editing message</span>
            <button type="button" onClick={() => { setEditTarget(null); setComposer(""); }}>
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <form onSubmit={send} className="flex items-end gap-1.5">
          <textarea
            ref={textareaRef}
            rows={1}
            value={composer}
            onChange={onComposerChange}
            onKeyDown={onKey}
            placeholder="Message… (@name to mention)"
            disabled={sending}
            className="flex-1 resize-none px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded focus:outline-none focus:border-blue-400 max-h-24"
          />
          <button
            type="submit"
            disabled={sending || !composer.trim()}
            className="shrink-0 p-1.5 rounded bg-blue-600 text-white disabled:bg-slate-200 hover:bg-blue-700"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── DM pane (mirrors MessagePane) ───────────────────────────────────────────

function DmPane({ thread, onClose, onShowList, listHidden }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const otherParticipant = (thread.participants || []).find((p) => p.user_id !== user?.id);
  const title = otherParticipant?.name || otherParticipant?.email || "Direct Message";

  const load = useCallback(async () => {
    if (!thread?.id) return;
    try {
      const data = await v2Api.dms.listMessages(thread.id);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("dm load failed", e);
    } finally {
      setLoading(false);
    }
  }, [thread?.id]);

  useEffect(() => {
    setMessages([]);
    setLoading(true);
    load();
  }, [thread?.id, load]);

  useEffect(() => {
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const msgCount = messages.length;
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgCount]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = composer.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const created = await v2Api.dms.sendMessage(thread.id, body);
      setMessages((prev) => [...prev, created]);
      setComposer("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50 shrink-0">
        {listHidden && (
          <button type="button" onClick={onShowList} className="text-slate-400 hover:text-slate-600 p-0.5" title="Show list">
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        <User className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{title}</span>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-0.5" title="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-xs">Loading…</span>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center py-10 gap-2 text-center px-4">
            <MessageSquare className="w-8 h-8 text-slate-200" />
            <p className="text-xs text-slate-500">No messages yet. Start the conversation.</p>
          </div>
        )}
        {messages.map((m) => (
          <MsgRow key={m.id} m={m} canEdit={false} canDelete={false} onEdit={() => {}} onDelete={() => {}} />
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="shrink-0 border-t border-slate-100 px-2 py-2 bg-white">
        <div className="flex items-end gap-1.5">
          <textarea
            rows={1}
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={onKey}
            placeholder={`Message ${title}…`}
            disabled={sending}
            className="flex-1 resize-none px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded focus:outline-none focus:border-blue-400 max-h-24"
          />
          <button
            type="submit"
            disabled={sending || !composer.trim()}
            className="shrink-0 p-1.5 rounded bg-blue-600 text-white disabled:bg-slate-200 hover:bg-blue-700"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main sidebar ────────────────────────────────────────────────────────────


// Parse the current route to extract a project ID or RFP ID for context-matching.
function useRouteContext() {
  const location = useLocation();
  const { pathname } = location;
  const projectMatch = pathname.match(/^\/v2\/projects\/([^/]+)/);
  const tenderMatch = pathname.match(/^\/(?:tender|c)\/([^/]+)/);
  return {
    projectId: projectMatch?.[1] ?? null,
    rfpId: tenderMatch?.[1] ?? null,
  };
}

// ─── Small shared list components ───────────────────────────────────────────

function SectionLabel({ label, icon }) {
  return (
    <div className="px-3 pt-2 pb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {icon} {label}
    </div>
  );
}

function ChannelRow({ ch, active, onClick, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors
        ${active ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}
    >
      {icon}
      <span className="flex-1 text-xs truncate font-medium">{ch.display_name}</span>
      {ch.unread_count > 0 && (
        <span className="shrink-0 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
          {ch.unread_count > 99 ? "99+" : ch.unread_count}
        </span>
      )}
    </button>
  );
}

// ─── Search results ───────────────────────────────────────────────────────────

function SearchResults({ q, channels, dmThreads, wsUsers, activeChannel, activeDmThread, user, onOpenChannel, onOpenDm }) {
  const rfpHits = channels.rfp.filter((c) => c.display_name.toLowerCase().includes(q));
  const deptHits = channels.department.filter((c) => c.display_name.toLowerCase().includes(q));
  const generalHit = channels.general && channels.general.display_name.toLowerCase().includes(q)
    ? channels.general : null;

  const threadHits = dmThreads.filter((t) => {
    const other = (t.participants || []).find((p) => p.user_id !== user?.id);
    return (other?.name || other?.email || "").toLowerCase().includes(q);
  });

  // Workspace users not already in a DM thread
  const threadUserIds = new Set(dmThreads.flatMap((t) =>
    (t.participants || []).map((p) => p.user_id)
  ));
  const userHits = wsUsers.filter((u) =>
    u.id !== user?.id &&
    !threadUserIds.has(u.id) &&
    ((u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
  );

  const empty = !generalHit && !rfpHits.length && !deptHits.length && !threadHits.length && !userHits.length;

  if (empty) {
    return <p className="px-4 py-6 text-xs text-slate-400 text-center">No results for "{q}"</p>;
  }

  return (
    <>
      {generalHit && (
        <>
          <SectionLabel label="General" />
          <ChannelRow ch={generalHit} active={activeChannel?.id === generalHit.id}
            onClick={() => onOpenChannel(generalHit)}
            icon={<Globe className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
        </>
      )}
      {rfpHits.length > 0 && (
        <>
          <SectionLabel label="Tenders" />
          {rfpHits.map((ch) => (
            <ChannelRow key={ch.id} ch={ch} active={activeChannel?.id === ch.id}
              onClick={() => onOpenChannel(ch)}
              icon={<Hash className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
          ))}
        </>
      )}
      {deptHits.length > 0 && (
        <>
          <SectionLabel label="Departments" icon={<Building2 className="w-3 h-3" />} />
          {deptHits.map((ch) => (
            <ChannelRow key={ch.id} ch={ch} active={activeChannel?.id === ch.id}
              onClick={() => onOpenChannel(ch)}
              icon={<Building2 className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
          ))}
        </>
      )}
      {(threadHits.length > 0 || userHits.length > 0) && (
        <>
          <SectionLabel label="Direct Messages" />
          {threadHits.map((t) => {
            const other = (t.participants || []).find((p) => p.user_id !== user?.id);
            return (
              <button key={t.id} type="button" onClick={() => onOpenDm(t)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors
                  ${activeDmThread?.id === t.id ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <User className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                <span className="flex-1 text-xs truncate font-medium">{other?.name || other?.email || "DM"}</span>
              </button>
            );
          })}
          {userHits.map((u) => (
            <button key={u.id} type="button"
              onClick={async () => {
                try {
                  const thread = await v2Api.dms.openThread([u.id]);
                  onOpenDm(thread);
                } catch { toast.error("Could not open DM"); }
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
            >
              <User className="w-3.5 h-3.5 shrink-0 text-slate-400" />
              <div className="min-w-0">
                <div className="text-xs truncate font-medium">{u.name || u.email}</div>
                {u.name && <div className="text-[10px] text-slate-400 truncate">{u.email}</div>}
              </div>
              <span className="shrink-0 text-[10px] text-slate-400 ml-auto">DM</span>
            </button>
          ))}
        </>
      )}
    </>
  );
}


export default function ChatSidebar() {
  const [mode, setMode] = useState("hidden");
  const [channels, setChannels] = useState({ general: null, rfp: [], department: [] });
  const [activeChannel, setActiveChannel] = useState(null);
  const [activeDmThread, setActiveDmThread] = useState(null);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState("");
  const [wsUsers, setWsUsers] = useState([]);
  const [dmThreads, setDmThreads] = useState([]);
  const { user } = useAuth();
  const routeCtx = useRouteContext();
  // Track previous route context to avoid re-opening when the user manually closes
  const lastAutoOpenRef = useRef(null);

  const width = mode === "open" ? OPEN_W
    : mode === "pane-only" ? PANE_W
    : LIST_W;

  // Broadcast mode so ChatWidget can react
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("v3.chatSidebar.state", { detail: { mode } }));
  }, [mode]);

  // Push main content when visible
  useEffect(() => {
    if (mode !== "hidden") {
      document.body.style.setProperty("--chat-sidebar-width", `${width}px`);
    } else {
      document.body.style.removeProperty("--chat-sidebar-width");
    }
    return () => document.body.style.removeProperty("--chat-sidebar-width");
  }, [mode, width]);

  // Listen for external toggle (fired by ChatWidget)
  useEffect(() => {
    const handler = () => setMode((m) => (m === "hidden" ? "list" : "hidden"));
    window.addEventListener("v3.chatSidebar.toggle", handler);
    return () => window.removeEventListener("v3.chatSidebar.toggle", handler);
  }, []);

  const loadChannels = useCallback(async () => {
    try {
      const data = await v2Api.channels.listUserChannels();
      setChannels({
        general: data.general || null,
        rfp: data.rfp || [],
        department: data.department || [],
      });
    } catch (e) {
      console.warn("channel list failed", e);
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Refresh unread counts every 30s
  useEffect(() => {
    const t = setInterval(loadChannels, 30_000);
    return () => clearInterval(t);
  }, [loadChannels]);

  // Load DM threads at top level so search can access them
  useEffect(() => {
    v2Api.dms.listThreads()
      .then((data) => setDmThreads(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const loadWsUsers = useCallback(async () => {
    if (wsUsers.length) return;
    try {
      const data = await v2Api.workspaceMembers();
      setWsUsers(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [wsUsers.length]);

  // Auto-open the relevant channel when navigating to a project or tender page.
  // Only fires when the route context changes (new project/rfp); won't fight the
  // user if they manually switch channels.
  useEffect(() => {
    const ctxKey = routeCtx.rfpId ?? routeCtx.projectId;
    if (!ctxKey || loadingChannels) return;
    if (lastAutoOpenRef.current === ctxKey) return; // same context, no re-open

    const match = routeCtx.rfpId
      ? channels.rfp.find((c) => c.rfp_id === routeCtx.rfpId)
      : null;

    if (match) {
      lastAutoOpenRef.current = ctxKey;
      setActiveChannel(match);
      setMode("open");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCtx.rfpId, routeCtx.projectId, loadingChannels, channels]);

  const openChannel = (ch) => {
    setActiveChannel(ch);
    setActiveDmThread(null);
    setMode((m) => m === "pane-only" ? "pane-only" : "open");
    // Keep the auto-open guard keyed to the current route context (rfpId/projectId)
    // so a manual channel switch doesn't let the auto-open re-fire for the same tender.
    lastAutoOpenRef.current = routeCtx.rfpId ?? routeCtx.projectId ?? ch.id;
    setChannels((prev) => {
      const update = (arr) => arr.map((c) => c.id === ch.id ? { ...c, unread_count: 0 } : c);
      return {
        general: prev.general?.id === ch.id ? { ...prev.general, unread_count: 0 } : prev.general,
        rfp: update(prev.rfp),
        department: update(prev.department),
      };
    });
  };

  const openDm = (thread) => {
    setActiveDmThread(thread);
    setActiveChannel(null);
    setMode((m) => m === "pane-only" ? "pane-only" : "open");
  };

  const closePane = () => {
    setActiveChannel(null);
    setActiveDmThread(null);
    setMode("hidden");
  };

  const totalUnread = [...(channels.general ? [channels.general] : []), ...channels.rfp, ...channels.department]
    .reduce((s, c) => s + (c.unread_count || 0), 0);

  // Broadcast unread count to ChatWidget
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("v3.chatSidebar.unread", { detail: { unread: totalUnread } }));
  }, [totalUnread]);

  // Guard: pane-only with nothing active → fall back to list
  useEffect(() => {
    if (mode === "pane-only" && !activeChannel && !activeDmThread) {
      setMode("list");
    }
  }, [mode, activeChannel, activeDmThread]);

  if (mode === "hidden") return null;

  const hasPane = mode === "open" || mode === "pane-only";
  const listVisible = mode === "list" || mode === "open";
  const listWidth = mode === "open" ? LIST_NARROW_W : LIST_W;

  return (
    <div
      style={{ width }}
      className="fixed right-0 top-0 h-screen bg-white border-l border-slate-200 shadow-xl flex z-40 transition-[width] duration-200"
    >
      {/* Channel list panel — hidden in pane-only mode */}
      {listVisible && (
        <div
          style={{ width: listWidth, minWidth: listWidth }}
          className="flex flex-col h-full overflow-hidden border-r border-slate-100"
        >
          {/* Header */}
          <div className="flex items-center gap-1 px-3 py-2.5 border-b border-slate-100 shrink-0">
            <span className="text-sm font-semibold text-slate-800 flex-1">Channels</span>
            {mode === "open" && (activeChannel || activeDmThread) && (
              <button type="button" onClick={() => setMode("pane-only")}
                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Hide list">
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            <button type="button" onClick={() => setMode("hidden")}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search box */}
          <div className="px-2 py-1.5 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
              <Search className="w-3 h-3 text-slate-400 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={loadWsUsers}
                placeholder="Search channels, people…"
                className="flex-1 text-xs bg-transparent focus:outline-none text-slate-700 placeholder:text-slate-400"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Channel + DM list */}
          <div className="flex-1 overflow-y-auto py-1">
            {loadingChannels ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            ) : search.trim() ? (
              /* ── Search results ── */
              <SearchResults
                q={search.toLowerCase()}
                channels={channels}
                dmThreads={dmThreads}
                wsUsers={wsUsers}
                activeChannel={activeChannel}
                activeDmThread={activeDmThread}
                user={user}
                onOpenChannel={openChannel}
                onOpenDm={openDm}
              />
            ) : (
              /* ── Normal grouped list ── */
              <>
                {/* General — pinned at top */}
                {channels.general && (
                  <ChannelRow ch={channels.general} active={activeChannel?.id === channels.general.id}
                    onClick={() => openChannel(channels.general)}
                    icon={<Globe className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
                )}

                {/* Tenders */}
                <SectionLabel label="Tenders" />
                {channels.rfp.map((ch) => (
                  <ChannelRow key={ch.id} ch={ch} active={activeChannel?.id === ch.id}
                    onClick={() => openChannel(ch)}
                    icon={<Hash className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
                ))}
                {channels.rfp.length === 0 && (
                  <p className="px-3 py-1 text-[11px] text-slate-400 italic">No tender channels</p>
                )}

                {/* Departments */}
                {channels.department.length > 0 && (
                  <>
                    <SectionLabel label="Departments" icon={<Building2 className="w-3 h-3" />} />
                    {channels.department.map((ch) => (
                      <ChannelRow key={ch.id} ch={ch} active={activeChannel?.id === ch.id}
                        onClick={() => openChannel(ch)}
                        icon={<Building2 className="w-3.5 h-3.5 shrink-0 text-slate-400" />} />
                    ))}
                  </>
                )}

                {/* DMs */}
                <DMSection
                  threads={dmThreads}
                  setThreads={setDmThreads}
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                  mode={mode}
                  activeDmThread={activeDmThread}
                  onOpen={openDm}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Message pane */}
      {hasPane && (activeChannel || activeDmThread) && (
        <div className="flex-1 h-full overflow-hidden">
          {activeChannel
            ? <MessagePane channel={activeChannel} onClose={closePane} onShowList={() => setMode("open")} listHidden={mode === "pane-only"} />
            : <DmPane thread={activeDmThread} onClose={closePane} onShowList={() => setMode("open")} listHidden={mode === "pane-only"} />
          }
        </div>
      )}
    </div>
  );
}

// ─── DM section ──────────────────────────────────────────────────────────────

function DMSection({ threads, setThreads, collapsed, setCollapsed, mode, activeDmThread, onOpen }) {
  const { user } = useAuth();
  const [showPicker, setShowPicker] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");

  const openPicker = async () => {
    if (!allUsers.length) {
      try {
        const data = await v2Api.workspaceMembers();
        setAllUsers(Array.isArray(data) ? data : []);
      } catch (e) {}
    }
    setPickerSearch("");
    setShowPicker(true);
  };

  const startDm = async (targetUser) => {
    setShowPicker(false);
    try {
      const thread = await v2Api.dms.openThread([targetUser.id]);
      setThreads((prev) => prev.find((t) => t.id === thread.id) ? prev : [thread, ...prev]);
      onOpen(thread);
    } catch (err) {
      toast.error("Could not open DM");
    }
  };

  const dmSectionOpen = !collapsed["dm"];
  const otherParticipant = (thread) =>
    (thread.participants || []).find((p) => p.user_id !== user?.id);

  const filteredUsers = allUsers.filter((u) => {
    if (u.id === user?.id) return false;
    const q = pickerSearch.toLowerCase();
    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="mb-1">
      {/* Section header */}
      <div className="flex items-center px-3 py-1">
        <button
          type="button"
          onClick={() => setCollapsed((p) => ({ ...p, dm: !p.dm }))}
          className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 flex-1"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${dmSectionOpen ? "" : "-rotate-90"}`} />
          Direct Messages
        </button>
        <button
          type="button"
          onClick={openPicker}
          title="New direct message"
          className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>

      {/* User picker */}
      {showPicker && (
        <div className="mx-2 mb-1 border border-slate-200 rounded-lg bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100">
            <input
              autoFocus
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Search people…"
              className="flex-1 text-xs px-1 py-0.5 bg-transparent focus:outline-none"
            />
            <button type="button" onClick={() => setShowPicker(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto">
            {filteredUsers.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-3">No users found</p>
            )}
            {filteredUsers.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => startDm(u)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
              >
                <User className="w-3 h-3 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{u.name || u.email}</div>
                  {u.name && <div className="text-[10px] text-slate-400 truncate">{u.email}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Thread list */}
      {dmSectionOpen && threads.map((t) => {
        const other = otherParticipant(t);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onOpen(t)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors
              ${activeDmThread?.id === t.id ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}
          >
            <User className="w-3.5 h-3.5 shrink-0 text-slate-400" />
            <span className="flex-1 text-xs truncate font-medium">
              {other?.name || other?.email || "Direct Message"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
